import type { AccessGrant } from "./people";

/**
 * The editable shape of an access grant, and the rules for changing it.
 *
 * **This file holds the client's only statement of the precedence rule.** It
 * mirrors `EffectiveScope.Resolve` in cts_api (REQ-ACL-4). The server's is the
 * boundary; this one decides what a tick box looks like, and the two disagreeing
 * would mean an admin ticking something that conveys the opposite of what they saw.
 *
 * "Exactly one copy" is asserted rather than claimed: the scan at the foot of
 * `grantDraft.test.ts` counts the places that answer reachability from the
 * exclusion list and requires that to be this file, once. It carries its own
 * negative control, because an equality against a one-item list is a check a dead
 * matcher passes.
 *
 * The rule, same as the server's:
 *
 *     visible = (allProperties ? everything : ⋃ granted properties' sensors)
 *               ∪ includeSensors \ excludeSensors
 *
 * with **exclusion winning over everything, including an explicit include**.
 */
export type GrantDraft = {
  allProperties: boolean;
  groupIds: string[];
  includeSensors: string[];
  excludeSensors: string[];
};

export const EMPTY_DRAFT: GrantDraft = {
  allProperties: false,
  groupIds: [],
  includeSensors: [],
  excludeSensors: [],
};

export function draftFromGrant(grant: AccessGrant | undefined): GrantDraft {
  if (!grant) return EMPTY_DRAFT;
  return {
    allProperties: !!grant.all_properties,
    groupIds: [...(grant.group_ids ?? [])],
    includeSensors: [...(grant.include_sensors ?? [])],
    excludeSensors: [...(grant.exclude_sensors ?? [])],
  };
}

export function draftToGrantBody(draft: GrantDraft) {
  return {
    all_properties: draft.allProperties,
    group_ids: draft.allProperties ? [] : draft.groupIds,
    include_sensors: draft.allProperties ? [] : draft.includeSensors,
    exclude_sensors: draft.allProperties ? [] : draft.excludeSensors,
  };
}

const without = (list: string[], value: string) => list.filter((v) => v !== value);
const with_ = (list: string[], value: string) =>
  list.includes(value) ? list : [...list, value];

/**
 * Whether a sensor is reachable under this draft.
 *
 * The single predicate the whole picker reads from, so a checkbox and the summary
 * beside it cannot disagree. `inGrantedProperty` is passed in rather than looked up
 * because the caller already knows which property it is rendering, and a sensor can
 * sit in more than one.
 */
export function isSensorReachable(
  draft: GrantDraft,
  serial: string,
  inGrantedProperty: boolean
): boolean {
  // Exclusion first and unconditionally: it beats all_properties, a granted
  // property, and an explicit include. Checking it last would let any of those
  // override it, which is the one ordering mistake that silently grants access.
  if (draft.excludeSensors.includes(serial)) return false;
  if (draft.allProperties) return true;
  if (draft.includeSensors.includes(serial)) return true;
  return inGrantedProperty;
}

/** Whether a property is granted whole. */
export function isPropertyGranted(draft: GrantDraft, groupId: string): boolean {
  return draft.allProperties || draft.groupIds.includes(groupId);
}

/**
 * Ticks or unticks a whole property.
 *
 * Unticking also drops any exclusions that belonged to it: an exclusion only means
 * something inside a granted property, and leaving them behind would re-apply them
 * if the property were ticked again later — a surprise an admin has no way to see.
 */
export function togglePropertyWith(
  draft: GrantDraft,
  groupId: string,
  serialsInProperty: string[]
): GrantDraft {
  if (isPropertyGranted(draft, groupId) && !draft.allProperties) {
    return {
      ...draft,
      groupIds: without(draft.groupIds, groupId),
      excludeSensors: draft.excludeSensors.filter(
        (s) => !serialsInProperty.includes(s)
      ),
    };
  }

  return {
    ...draft,
    allProperties: false,
    groupIds: with_(draft.groupIds, groupId),
    // Ticking the property supersedes any per-sensor includes from inside it.
    includeSensors: draft.includeSensors.filter(
      (s) => !serialsInProperty.includes(s)
    ),
  };
}

/**
 * Ticks or unticks one sensor.
 *
 * Which list it lands in depends on whether its property is granted, which is what
 * makes "property-level with exceptions" expressible at all:
 *
 *  - inside a granted property, unticking records an **exclusion**;
 *  - outside one, ticking records an **include**.
 *
 * Re-ticking inside a granted property removes the exclusion rather than adding an
 * include, so the two lists cannot both name the same serial — the state where the
 * server's precedence rule becomes observable, and the one an admin would find
 * hardest to reason about.
 */
export function toggleSensor(
  draft: GrantDraft,
  serial: string,
  inGrantedProperty: boolean
): GrantDraft {
  const reachable = isSensorReachable(draft, serial, inGrantedProperty);

  if (reachable) {
    // Turning it off: drop an include if that is what made it reachable,
    // otherwise record an exclusion.
    if (draft.includeSensors.includes(serial)) {
      return { ...draft, includeSensors: without(draft.includeSensors, serial) };
    }
    return { ...draft, excludeSensors: with_(draft.excludeSensors, serial) };
  }

  // Turning it on: lifting an exclusion is enough when the property already
  // grants it; otherwise it needs an include of its own.
  if (draft.excludeSensors.includes(serial)) {
    const lifted = without(draft.excludeSensors, serial);
    const nowReachable = isSensorReachable(
      { ...draft, excludeSensors: lifted },
      serial,
      inGrantedProperty
    );
    return nowReachable
      ? { ...draft, excludeSensors: lifted }
      : {
          ...draft,
          excludeSensors: lifted,
          includeSensors: with_(draft.includeSensors, serial),
        };
  }

  return { ...draft, includeSensors: with_(draft.includeSensors, serial) };
}

/** Turns tenant-wide access on, or back to an empty explicit grant. */
export function toggleAllProperties(draft: GrantDraft): GrantDraft {
  if (draft.allProperties) return { ...EMPTY_DRAFT };
  return { ...EMPTY_DRAFT, allProperties: true };
}

/** Whether a draft conveys nothing at all. */
export function grantsNothing(draft: GrantDraft): boolean {
  return (
    !draft.allProperties &&
    draft.groupIds.length === 0 &&
    draft.includeSensors.length === 0
  );
}
